import * as XLSX from 'xlsx';
import { supabase } from './supabase';
import { fetchAll } from './save';
import { logAudit } from '../hooks/useAudit';

/*
  Take everything out.

  Job 17, part two, 7 September 2026. The owner asked for three things about
  data and this is the middle one: "I can pull/export all the data at anytime".

  **Why it matters more than it sounds.** `docs/BACKUPS.md` says it plainly:
  every backup this project has lives inside the same Supabase account as the
  thing it is backing up, and none of them can be downloaded. So until this
  existed, **there was no copy of OnHand anywhere the owner controls.** The app
  could export a list at a time, and there was no "give me everything".

  **What this produces.** One spreadsheet file, one sheet per table, plus a
  first sheet that is the receipt: every table, how many rows came back, and
  whether the three-year rule touches it. The receipt is what makes the file
  checkable later, and it is the same idea as `scripts/backup-manifest.sql`,
  which until now was a query somebody had to remember to run by hand.

  **What it does not contain, said here so it is not discovered in an
  emergency.** The sign-in accounts, which live in Supabase's own system and
  not in these tables, and the uploaded logos, which live in file storage. The
  pgAdmin route in `docs/BACKUPS.md` gets both and this does not. Both routes
  are real and they are for different days: this one is a press, that one is a
  half hour with a desktop application.
*/

/*
  Every table, and which kind of thing it holds.

  **`record` means something that happened on a date**, and those are what the
  three-year rule removes. **`thing` means what the business currently is**, and
  age means nothing to those: a location added four years ago is still the
  location. The receipt prints the difference, so the file itself says which of
  its sheets will one day be shorter.

  The list is written out rather than read from the database on purpose. A new
  table has to be added here deliberately, which is a line of work, and the
  alternative is a file that quietly stops covering something.
*/
export const EVERYTHING = [
  { table: 'organizations',          label: 'Sites',                   kind: 'thing'  },
  { table: 'regions',                label: 'Regions',                 kind: 'thing'  },
  { table: 'warehouses',             label: 'Locations',               kind: 'thing'  },
  { table: 'accounts',               label: 'Accounts',                kind: 'thing'  },
  { table: 'account_reps',           label: 'Reps on accounts',        kind: 'thing'  },
  { table: 'profiles',               label: 'People',                  kind: 'thing'  },
  { table: 'org_members',            label: 'People in this site',     kind: 'thing'  },
  { table: 'user_regions',           label: 'People and regions',      kind: 'thing'  },
  { table: 'user_warehouses',        label: 'People and locations',    kind: 'thing'  },
  { table: 'user_referral_sources',  label: 'People and referrals',    kind: 'thing'  },
  { table: 'referral_sources',       label: 'Referral sources',        kind: 'thing'  },
  { table: 'items',                  label: 'Item catalog',            kind: 'thing'  },
  { table: 'item_locations',         label: 'Where items sit',         kind: 'thing'  },
  { table: 'vendor_aliases',         label: 'Vendor list',             kind: 'thing'  },
  { table: 'app_settings',           label: 'Site settings',           kind: 'thing'  },
  { table: 'release_notes',          label: 'Release notes',           kind: 'thing'  },
  { table: 'site_terms',             label: 'Terms',                   kind: 'thing'  },
  { table: 'user_active_org',        label: 'Which site each person is in', kind: 'thing' },
  { table: 'user_preferences',       label: 'What each person has set', kind: 'thing'  },

  { table: 'count_cycles',           label: 'Count cycles',            kind: 'record' },
  { table: 'inventory_counts',       label: 'Count sheets',            kind: 'record' },
  { table: 'count_line_items',       label: 'Counted lines',           kind: 'record' },
  { table: 'count_line_item_history',label: 'Line changes',            kind: 'record' },
  { table: 'historical_counts',      label: 'Prior counts',            kind: 'record' },
  { table: 'order_runs',             label: 'Order runs',              kind: 'record' },
  { table: 'order_run_lines',        label: 'Order run lines',         kind: 'record' },
  { table: 'client_orders',          label: 'Client orders',           kind: 'record' },
  { table: 'client_order_lines',     label: 'Client order lines',      kind: 'record' },
  { table: 'alerts',                 label: 'Alerts',                  kind: 'record' },
  { table: 'todos',                  label: 'Tasks',                   kind: 'record' },
  { table: 'audit_logs',             label: 'Audit log',               kind: 'record' },

  // Regional Product Movement's history, in the copy and ageing out like every
  // other record: the owner's answer of 18 September 2026 (the audit after the
  // rebrand, question 15). Until then these three were in neither, and the list
  // above said it covered every table while the database held 36 and it named
  // 33.
  { table: 'movement_runs',          label: 'Movement runs',           kind: 'record' },
  { table: 'movement_run_pages',     label: 'Movement run pages',      kind: 'record' },
  { table: 'movement_run_lines',     label: 'Movement run lines',      kind: 'record' },

  /*
    **These two look like records and are not, and the receipt said the wrong
    thing about them until the file was opened and read back on 7 September
    2026.** Somebody having read a release note, or accepted the terms, is a
    thing that is still true rather than an event that happened: removing it
    would have OnHand stop everybody at the door to accept terms they accepted
    years ago. So they stay, and the removal in the database does not touch
    them, which is what the receipt now agrees with.
  */
  { table: 'release_note_reads',     label: 'Release notes read',      kind: 'thing'  },
  { table: 'site_terms_acceptances', label: 'Terms accepted',          kind: 'thing'  },
];

/*
  The tables the three-year removal actually deletes from, named here so the
  receipt cannot promise something the database does not do. It is the list in
  `retention_run()`, and the test below holds the two in step.
*/
export const AGES_OUT = [
  'count_cycles', 'inventory_counts', 'count_line_items', 'count_line_item_history',
  'historical_counts', 'order_runs', 'order_run_lines', 'client_orders',
  'client_order_lines', 'alerts', 'todos', 'audit_logs',
  'movement_runs', 'movement_run_pages', 'movement_run_lines',
];

/*
  A cell a spreadsheet can hold.

  A date arrives as text and stays text. A setting whose value is a small
  structure, like a site's modules or an audit line's details, is written out as
  the text of that structure rather than as `[object Object]`, which is what a
  spreadsheet does with it otherwise and which is not a thing anybody can read.
*/
export function cellValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return v;
}

/*
  Rows to a grid, with the column names across the top.

  The columns come from the first row and then anything a later row has that the
  first did not, so a table where one row carries an extra field does not lose
  it. A table with no rows still gets a sheet, with one line saying so, because a
  missing sheet and an empty one read very differently to somebody checking the
  file a year from now.
*/
export function gridFor(rows) {
  if (!rows || rows.length === 0) return [['No rows']];
  const cols = [];
  const seen = new Set();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) { seen.add(k); cols.push(k); }
    }
  }
  return [cols, ...rows.map(r => cols.map(c => cellValue(r[c])))];
}

// Excel refuses a sheet name over 31 characters, and refuses these characters
// in one at all. No table is close to the limit today; this is here so that
// adding one that is does not produce a file that will not open.
export function sheetName(name) {
  return String(name).replace(/[\\/?*[\]:]/g, '-').slice(0, 31);
}

/*
  Read every table, a page at a time.

  **A request brings back about a thousand rows and says nothing about the
  rest**, which is why this goes through `fetchAll` rather than asking once. The
  item catalog alone is over that line.

  **A table that could not be read is recorded as refused, never as empty.** The
  database answers an unpermitted read with no rows and no complaint, so a sheet
  reading "No rows" could mean the table is empty, or that this person is not
  allowed to see it, or that the table has been renamed. Those are three
  different facts and the receipt says which.
*/
export async function readEverything(onStep) {
  const out = [];
  for (let i = 0; i < EVERYTHING.length; i += 1) {
    const def = EVERYTHING[i];
    if (onStep) onStep({ index: i, total: EVERYTHING.length, label: def.label });
    const { data, error } = await fetchAll(() => supabase.from(def.table).select('*'));
    out.push({ ...def, rows: error ? null : (data || []), error: error ? error.message : null });
  }
  return out;
}

/*
  The receipt: the first sheet of the file, and the reason the file is checkable.

  It is what `scripts/backup-manifest.sql` was for, except nobody has to remember
  to run it. Reading it back against a fresh one is how you find out that a copy
  taken in March was short.
*/
export function receiptGrid(results, { company, taken, years }) {
  const rows = [
    ['OnHand: everything in this site'],
    ['Site', company || ''],
    ['Taken', taken],
    ['Kept for', years ? years + ' years' : ''],
    [],
    ['Sheet', 'Table', 'Rows', 'Removed after ' + (years || 3) + ' years', 'Notes'],
  ];
  let total = 0;
  for (const r of results) {
    const ok = r.error === null;
    if (ok) total += r.rows.length;
    rows.push([
      r.label,
      r.table,
      ok ? r.rows.length : '',
      r.kind === 'record' ? 'Yes' : 'No',
      ok ? '' : 'COULD NOT BE READ: ' + r.error,
    ]);
  }
  rows.push([]);
  rows.push(['Total rows', '', total]);
  rows.push([]);
  rows.push(['This file does NOT contain the sign-in accounts or the uploaded logos.']);
  rows.push(['Those are fetched separately. The backups guide in this project says how.']);
  rows.push(['Rows are what the person taking this copy is allowed to see.']);
  rows.push(['Per-person settings, like a saved column arrangement, show only their own.']);
  return rows;
}

/*
  Build the workbook. Separate from the reading and the saving so a test can
  look at what would be written without a browser and without a database.
*/
export function buildWorkbook(results, receipt) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(receipt), sheetName('What is in this file'));
  for (const r of results) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(r.error === null ? gridFor(r.rows) : [['COULD NOT BE READ'], [r.error]]),
      sheetName(r.label),
    );
  }
  return wb;
}

export function fileStamp(d = new Date()) {
  const p = n => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

/*
  The whole thing, as one press.

  It records that a copy was taken, in the audit log, which is what makes "when
  was the last one" a question the screen can answer rather than a thing the
  owner has to remember.
*/
export async function takeEverything({ company, years, profile, onStep } = {}) {
  const results = await readEverything(onStep);
  const taken = new Date();
  const receipt = receiptGrid(results, { company, taken: taken.toISOString(), years });
  const wb = buildWorkbook(results, receipt);
  const name = 'OnHand_Everything_' + String(company || 'site').replace(/[^A-Za-z0-9]+/g, '_')
             + '_' + fileStamp(taken) + '.xlsx';
  XLSX.writeFile(wb, name);

  const refused = results.filter(r => r.error !== null);
  const total = results.reduce((n, r) => n + (r.rows ? r.rows.length : 0), 0);
  await logAudit(profile, 'EXPORT_EVERYTHING', 'data', {
    target_name: company || null,
    details: { rows: total, tables: results.length, refused: refused.length },
  });
  return { results, refused, total, name };
}

/*
  When the last full copy was taken, and whether it has gone stale.

  **`docs/BACKUPS.md` says the question is not "how long has it been"**, it is
  "if the project vanished right now, what would we have to do again by hand".
  A month is the floor it names, and a closed count cycle is the trigger it
  calls the most important, because a cycle's counts happened once, in person,
  in warehouses, and nobody can reconstruct them from memory.
*/
export async function lastFullCopy() {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('created_at, user_name, details')
    .eq('action', 'EXPORT_EVERYTHING')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) return { when: null, error: error.message };
  return { when: data && data[0] ? data[0] : null, error: null };
}

export const STALE_DAYS = 30;

export function copyAge(whenIso, now = new Date()) {
  if (!whenIso) return { days: null, stale: true, sentence: 'No full copy of this site has ever been taken.' };
  const days = Math.floor((now - new Date(whenIso)) / 86400000);
  if (days <= 0) return { days: 0, stale: false, sentence: 'The last full copy was taken today.' };
  const said = days === 1 ? 'yesterday' : days + ' days ago';
  return {
    days,
    stale: days >= STALE_DAYS,
    sentence: 'The last full copy was taken ' + said + '.',
  };
}
